interface InitialsAvatarProps {
  /** Source name to derive initials from. Empty/whitespace-only renders
   *  a single "?" so the disc never looks broken. Ignored when an
   *  explicit `initials` prop is passed. */
  name: string;
  /** Optional pre-computed initials to render instead of deriving from
   *  `name`. Use when the server already produced them (e.g. session
   *  initials with custom multi-word handling). */
  initials?: string;
  /** Display size in pixels. Defaults to 32. The font scales with size. */
  size?: number;
  /** Optional accessible label. Defaults to `name`. */
  alt?: string;
}

/**
 * Round 2-letter avatar on the brand teal gradient. Same visual
 * treatment for every entity — a customer, a user, a deal — so avatars
 * across the app feel like one family.
 *
 * Both customer-style ("Herwig Automotive" → "HA") and short-name
 * ("PNC" → "PN") cases come out reasonable. Connector words ("the",
 * "of", "and", …) are skipped so "The Coca-Cola Company" → "CC".
 */
export function InitialsAvatar({
  name,
  initials,
  size = 32,
  alt,
}: InitialsAvatarProps) {
  const text = initials ?? deriveInitials(name);
  const fontSize = Math.max(10, Math.round(size * 0.42));
  return (
    <div
      role="img"
      aria-label={alt ?? name}
      className="flex shrink-0 items-center justify-center rounded-full font-bold text-white"
      style={{
        width: size,
        height: size,
        fontSize,
        // brand-accent (not primary) so the disc stays teal in monochrome
        // mode — otherwise primary collapses to near-white and the white
        // initials vanish. Avatars are "one brand-teal family" across themes.
        background:
          "linear-gradient(135deg, var(--brand-accent), var(--brand-accent-hover))",
      }}
    >
      {text}
    </div>
  );
}

/**
 * Up-to-2-char initials derived from a name. Skips short connector words
 * so multi-word brand names land on the meaningful tokens.
 */
export function deriveInitials(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const skip = new Set(["the", "a", "an", "of", "and", "&"]);
  const words = cleaned
    .split(/\s+/)
    .filter((w) => !skip.has(w.toLowerCase()))
    .filter(Boolean);
  if (words.length === 0) return cleaned.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
